import { useEffect } from 'react';
import useForm from 'src/hooks/useForm';
import useAuth from 'src/hooks/useAuth';
import {
  useGetFormConfig,
  processFoodRunFormData,
  useAddFoodRunSchema,
} from 'src/views/schedule/FoodRun/helper';
import { foodRun as foodRunService } from 'src/backend';
import useEventBus from 'src/hooks/useEventBus';
import { useSnackbar } from 'notistack';
import SocketIO from 'src/backend/SocketIO';
import useOrgsForFoodRunCreation from 'src/hooks/useOrgsForFoodRunCreation';
import _find from 'lodash/find';
import { getLocaleUnit, Weight } from 'careit';
import useVehicles from 'src/hooks/useVehicles';
import getTimeZoneIdFromCoords from 'src/utils/getTimeZoneIdFromCoords';
import _isNumber from 'lodash/isNumber';
import useDriverAvailability from 'src/hooks/useDriverAvailability';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { uploadImages } from 'src/components/form/UploadImages';
import { parseUTC } from 'src/utils/timezones';
import _ from 'lodash';
import { processDaysOfTheWeek } from 'src/utils/recurring';
import { useGetVehicleLabels } from 'src/constants';

export const parseFoodRunForCopy = (foodRun, user, vehiclesMap) => {
  const donorLocation = JSON.parse(foodRun.donor_location || '{}');
  const recipientLocation = JSON.parse(foodRun.receiver_location || '{}');

  const endDate = parseUTC(foodRun?.end_date, user);
  const isPast = endDate < new Date();

  // Convert to preferred unit to handle unit conversions
  const foodWeights = _.mapValues(
    _.keyBy(JSON.parse(foodRun.food_weight ?? '[]'), ({ food_type }) =>
      _.toLower(food_type)
    ),
    ({ estimated_weight, estimated_weight_unit }) => {
      return {
        value:
          estimated_weight === null
            ? null
            : new Weight(estimated_weight, 'lbs')
                .as(estimated_weight_unit)
                .valueOf()
                .toString(),
        unit: estimated_weight_unit,
      };
    }
  );

  return {
    title: foodRun.title || '',
    description: foodRun.description || '',
    foodTypes: isPast ? {} : foodWeights,
    donor: foodRun.donor_team,
    receiver: foodRun.receiver_team,
    vehicleSize: vehiclesMap.find((item) => foodRun.vehicle === item.vehicle)
      ?.value,
    pickup_address: {
      lat: donorLocation.lat,
      lng: donorLocation.lng,
      formattedAddress: donorLocation.formatted_address,
      name: donorLocation.place,
      streetAddress: donorLocation.street_address,
      city: donorLocation.city,
      province: donorLocation.province,
      country: donorLocation.country,
      zipCode: donorLocation.zip_code,
      placeId: donorLocation.place_id,
      timeZoneId: donorLocation.timezone_id,
    },
    dest_address: {
      lat: recipientLocation.lat,
      lng: recipientLocation.lng,
      formattedAddress: recipientLocation.formatted_address,
      name: recipientLocation.place,
      streetAddress: recipientLocation.street_address,
      city: recipientLocation.city,
      province: recipientLocation.province,
      country: recipientLocation.country,
      zipCode: recipientLocation.zip_code,
      placeId: recipientLocation.place_id,
      timeZoneId: recipientLocation.timezone_id,
    },
    custom_addresses: Boolean(foodRun.is_food_run),
    pickupInstructions: foodRun.pickup_instructions || '',
    dropoffInstructions: foodRun.dropoff_instructions || '',
    images: JSON.parse(foodRun.images || '[]'),
    occurrenceType: 'onetime',
    ...(isPast
      ? {}
      : {
          startTime: parseUTC(foodRun?.start_date, user),
          endTime: parseUTC(foodRun?.end_date, user),
          startDate: parseUTC(foodRun.start_date, user),
          endDate: endDate,
        }),
  };
};

export default function CreateFoodRun() {
  const vehiclesMap = useGetVehicleLabels();
  const { t } = useTranslation('translation');
  const AddFoodRunSchema = useAddFoodRunSchema();
  const socket = SocketIO.getIO();
  const { user, invalidateUserData } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { vehicles = [], invalidate: VehicleRefresh } = useVehicles();
  const emitFetchFoodRuns = useEventBus('foodrun', 'fetch', null, null, {
    crossTabChannel: true,
  });
  const {
    donors,
    recipients,
    invalidateMyOrgs,
    isMyRecipientsEmpty,
    fetchNearByRecipients,
  } = useOrgsForFoodRunCreation({
    lat: user.lat,
    lng: user.lng,
    fetchOnMount: false,
  });
  const { weight: preferredUnit } = getLocaleUnit(user);

  const { checkAvailabilityForDrivers, drivers } = useDriverAvailability();
  const formConfig = useGetFormConfig(
    donors,
    recipients,
    user.timezone_id,
    invalidateMyOrgs,
    preferredUnit,
    vehicles,
    drivers
  );
  const { handleOpen, form, state, open, setFieldState, updateBulkState } =
    useForm({
      noClose: true,
      handleSave: async (data) => {
        let pickupTimeZoneId = null;
        if (_isNumber(data?.pickup_address?.lat)) {
          pickupTimeZoneId = await getTimeZoneIdFromCoords(
            data?.pickup_address
          );
        }
        let destTimeZoneId = null;
        if (_isNumber(data?.dest_address?.lat)) {
          destTimeZoneId = await getTimeZoneIdFromCoords(data?.dest_address);
        }

        const newFoodRun = processFoodRunFormData({
          ...data,
          pickup_address: {
            ...data.pickup_address,
            timeZoneId: pickupTimeZoneId,
          },
          dest_address: {
            ...data.dest_address,
            timeZoneId: destTimeZoneId,
          },
          timezone_id: pickupTimeZoneId ?? user.timezone_id,
        });
        const patternId = newFoodRun.patternId;

        const s3Folder = newFoodRun.isRecurring
          ? `recurring-pattern/${patternId}`
          : `donation/${newFoodRun.donationId}`;

        const images = await uploadImages({
          images: data.images || [],
          folder: s3Folder,
          source: 'Create food runs',
        });

        newFoodRun.images = images;

        foodRunService
          .create(newFoodRun)
          .then(() => {
            emitFetchFoodRuns();
            invalidateUserData();
            socket.emit('DONATION-UPDATE', {
              patternId,
              donationId: null,
              teamId: user.team_id,
              type: 'FOOD-RUN',
            });
          })
          .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }));
      },
      form: formConfig,
      title: t('donations.create-food-run'),
      schema: AddFoodRunSchema(donors, recipients),
      defaultState: {
        daysOfTheWeek: [0], // later, this initial value will be changed before we send the form
      },
    });

  useEffect(() => {
    if (open) {
      VehicleRefresh();
    }
  }, [open]);
  useEffect(() => {
    if (state.donor) {
      const selectedDonor = _find(
        donors,
        (team) => team.team_id === state.donor
      );
      if (isMyRecipientsEmpty && selectedDonor) {
        fetchNearByRecipients({
          donorLat: selectedDonor.lat,
          donorLng: selectedDonor.lng,
          donorTeamId: selectedDonor.team_id,
        });
      } else if (selectedDonor) {
        invalidateMyOrgs(selectedDonor.team_id);
      }
      if (_isNumber(selectedDonor?.lat)) {
        setFieldState('pickup_address', {
          lat: selectedDonor.lat,
          lng: selectedDonor.lng,
          formattedAddress: selectedDonor.formatted_address,
          name: selectedDonor.place,
          streetAddress: selectedDonor.street_address,
          city: selectedDonor.city,
          province: selectedDonor.province,
          country: selectedDonor.country,
          zipCode: selectedDonor.zip_code,
          placeId: selectedDonor.place_id,
          timeZoneId: selectedDonor.timezone_id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.donor]);

  useEffect(() => {
    if (state.receiver) {
      const selectedReceiver = _find(
        recipients,
        (team) => team.team_id === state.receiver
      );
      if (_isNumber(selectedReceiver?.lat))
        setFieldState('dest_address', {
          lat: selectedReceiver.lat,
          lng: selectedReceiver.lng,
          formattedAddress: selectedReceiver.formatted_address,
          name: selectedReceiver.place,
          streetAddress: selectedReceiver.street_address,
          city: selectedReceiver.city,
          province: selectedReceiver.province,
          country: selectedReceiver.country,
          zipCode: selectedReceiver.zip_code,
          placeId: selectedReceiver.place_id,
          timeZoneId: selectedReceiver.timezone_id,
        });
    }
  }, [state.receiver]);

  useEffect(() => {
    if (open) {
      invalidateMyOrgs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parseFoodRunForCopy = (foodRun) => {
    const donorLocation = JSON.parse(foodRun.donor_location || '{}');
    const recipientLocation = JSON.parse(foodRun.receiver_location || '{}');

    const endDate = parseUTC(foodRun?.end_date, user);
    const isPast = endDate < new Date();

    // Convert to preferred unit to handle unit conversions
    const foodWeights = _.mapValues(
      _.keyBy(JSON.parse(foodRun.food_weight ?? '[]'), ({ food_type }) =>
        _.toLower(food_type)
      ),
      ({ estimated_weight, estimated_weight_unit }) => {
        return {
          value:
            estimated_weight === null
              ? null
              : new Weight(estimated_weight, 'lbs')
                  .as(estimated_weight_unit)
                  .valueOf()
                  .toString(),
          unit: estimated_weight_unit,
        };
      }
    );

    return {
      title: foodRun.title || '',
      description: foodRun.description || '',
      foodTypes: isPast ? {} : foodWeights,
      donor: foodRun.donor_team,
      receiver: foodRun.receiver_team,
      vehicleSize: vehiclesMap.find((item) => foodRun.vehicle === item.vehicle)
        ?.value,
      pickup_address: {
        lat: donorLocation.lat,
        lng: donorLocation.lng,
        formattedAddress: donorLocation.formatted_address,
        name: donorLocation.place,
        streetAddress: donorLocation.street_address,
        city: donorLocation.city,
        province: donorLocation.province,
        country: donorLocation.country,
        zipCode: donorLocation.zip_code,
        placeId: donorLocation.place_id,
        timeZoneId: donorLocation.timezone_id,
      },
      dest_address: {
        lat: recipientLocation.lat,
        lng: recipientLocation.lng,
        formattedAddress: recipientLocation.formatted_address,
        name: recipientLocation.place,
        streetAddress: recipientLocation.street_address,
        city: recipientLocation.city,
        province: recipientLocation.province,
        country: recipientLocation.country,
        zipCode: recipientLocation.zip_code,
        placeId: recipientLocation.place_id,
        timeZoneId: recipientLocation.timezone_id,
      },
      custom_addresses: Boolean(foodRun.is_food_run),
      pickupInstructions: foodRun.pickup_instructions || '',
      dropoffInstructions: foodRun.dropoff_instructions || '',
      images: JSON.parse(foodRun.images || '[]'),
      occurrenceType: 'onetime',
      ...(isPast
        ? {}
        : {
            startTime: parseUTC(foodRun?.start_date, user),
            endTime: parseUTC(foodRun?.end_date, user),
            startDate: parseUTC(foodRun.start_date, user),
            endDate: endDate,
          }),
    };
  };

  useEventBus(
    'foodrun',
    'create',
    (initialData) => {
      if (initialData) {
        const parsedData = parseFoodRunForCopy(initialData);
        updateBulkState({
          ...state,
          ...parsedData,
        });
      }
      handleOpen();
    },
    []
  );
  useEventBus('foodrun', 'invalidate-orgs', invalidateMyOrgs, []);
  useEffect(() => {
    if (
      open &&
      state.startDate &&
      state.startTime &&
      state.endDate &&
      state.endTime
    ) {
      const startDate = moment(state.startDate)
        .hour(state.startTime.getHours())
        .minute(state.startTime.getMinutes())
        .second(state.startTime.getSeconds())
        .toISOString();
      const endDate = moment(
        state.occurrenceType === 'onetime' ? state.startDate : state.endDate
      )
        .hour(state.endTime.getHours())
        .minute(state.endTime.getMinutes())
        .second(state.endTime.getSeconds())
        .toISOString();
      const daysOfTheWeek = processDaysOfTheWeek(state);
      checkAvailabilityForDrivers({
        pickupTime: startDate,
        endTime: endDate,
        occurrenceType: state.occurrenceType,
        interval: state.interval,
        daysOfTheWeek: daysOfTheWeek,
        dayOfTheMonth: state.dayOfTheMonth,
      });
    }
  }, [
    state.startDate,
    state.startTime,
    state.endDate,
    state.endTime,
    state.occurrenceType,
    state.interval,
    // days of the week state
    state['repeat-0'],
    state['repeat-1'],
    state['repeat-2'],
    state['repeat-3'],
    state['repeat-4'],
    state['repeat-5'],
    state['repeat-6'],
    state.dayOfTheMonth,
    open,
  ]);

  return <>{form()}</>;
}
